import { useMemo } from "react";
import { UneligibleTradeReason } from "@cede/types";
import { API_ENDPOINT, getCurrentEpoch, getNextEpoch } from "@cede/utils";
import { useWalletAgeEligibility } from "./useWalletAgeEligibility";
import { CAMPAIGN_STATUS } from "@/components/features/campaigns/types";
import { useCampaign } from "@/hooks/campaign/useCampaign";
import { useLeaderboard } from "@/hooks/campaign/useLeaderboard";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useValidateHubDefiAddress } from "@/hooks/user/useValidateHubDefiAddress";
import { useUserVolumeInWindow } from "@/hooks/volumeTracker/useUserVolumeInWindow";
import { isOnchainCampaign } from "@/utils/campaign";
import { QueryClient, useQuery } from "@tanstack/react-query";

const SHOULD_DISABLE_TRADE_QUERY_KEY = "shouldDisableTradeFromApi";
const EMPTY_DEFI_ADDRESSES: string[] = [];

export function invalidateShouldDisableTrade(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [SHOULD_DISABLE_TRADE_QUERY_KEY] });
}

type UseAccessTradeCampaignProps = {
  campaignName: string;
};

/**
 * @description
 * This hook is used to check if the user has access to trade a campaign.
 * It checks:
 * - If the user has a valid defi address
 * - If all the spots aren't taken
 */
export const useTradeCampaignAccess = ({ campaignName }: UseAccessTradeCampaignProps) => {
  const api = useApi();
  const { hubUser, auth } = useHubUser();
  const { campaign: campaignMetadata, isLoading: isCampaignLoading } = useCampaign(campaignName);
  const currentEpoch = campaignMetadata ? getCurrentEpoch(campaignMetadata) : null;
  const { leaderboard, isLoading: isLeaderboardLoading } = useLeaderboard(campaignName, currentEpoch || undefined);

  const { validationResponse, isLoading: isValidationLoading } = useValidateHubDefiAddress({
    campaignName,
  });

  const { nextWindowDate, isLoading: isUserVolumeInWindowLoading } = useUserVolumeInWindow({
    campaignName,
    epoch: currentEpoch || undefined,
    enabled: !!campaignMetadata && campaignMetadata.status === CAMPAIGN_STATUS.LIVE,
  });

  // Check if this is an onchain (DEX) campaign
  const isOnchain = isOnchainCampaign(campaignMetadata);

  // Check wallet age eligibility for onchain campaigns
  // Wallets must have at least one transaction older than 2 weeks to participate
  const {
    isEligible: isWalletAgeEligible,
    isLoading: isWalletAgeLoading,
    data: walletAgeData,
  } = useWalletAgeEligibility({
    walletAddress: hubUser?.defiAddress ?? "",
    enabled: isOnchain && !!hubUser?.defiAddress,
  });

  const { data: shouldDisableTrade } = useQuery({
    queryKey: [SHOULD_DISABLE_TRADE_QUERY_KEY, leaderboard, hubUser?.id, currentEpoch?.id],
    queryFn: async () => {
      if (!currentEpoch || isLeaderboardLoading || !campaignName || !leaderboard) return false;

      const { shouldDisableTrade } = await api.getWithAuth(auth, API_ENDPOINT.HUB_LEADERBOARD_SHOULD_DISABLE_TRADE, {
        queryParams: {
          campaign: campaignName,
        },
      });
      return shouldDisableTrade;
    },
    enabled: !isLeaderboardLoading && !!leaderboard && !!hubUser && !!currentEpoch,
  });

  // FUTURE campaigns are accessible only when autopilot is enabled on the
  // upcoming epoch — that's the one and only thing the user can usefully do
  // before the campaign starts. Autopilot config is resolved epoch-group-first,
  // then campaign-level, matching `useAutomaticStreakConfig`.
  const isAutopilotEnabledOnNextEpoch = useMemo(() => {
    if (!campaignMetadata) return false;
    const nextEpoch = getNextEpoch(campaignMetadata);
    if (!nextEpoch) return false;
    const epochGroup = campaignMetadata.epochGroups?.find((eg) => eg.id === nextEpoch.epochGroupId);
    return Boolean(epochGroup?.autopilotConfig ?? campaignMetadata.autopilotConfig);
  }, [campaignMetadata]);

  const campainStatusIsValidForAccess = useMemo(() => {
    if (!campaignMetadata) return undefined;

    if (campaignMetadata.status === CAMPAIGN_STATUS.PAUSED || campaignMetadata.status === CAMPAIGN_STATUS.PAST) {
      return false;
    }

    if (campaignMetadata.status === CAMPAIGN_STATUS.FUTURE && !isAutopilotEnabledOnNextEpoch) {
      return false;
    }

    return true;
  }, [campaignMetadata, isAutopilotEnabledOnNextEpoch]);

  /**
   * @remarks
   * Get the reason why the user doesn't have access to the campaign
   */
  const getReason = (): UneligibleTradeReason | undefined => {
    if (hubUser?.isBanned) {
      return UneligibleTradeReason.USER_BANNED;
    }
    // Check wallet age eligibility for onchain campaigns
    if (isOnchain && isWalletAgeEligible === false) {
      return UneligibleTradeReason.WALLET_TOO_NEW;
    }
    if (shouldDisableTrade && shouldDisableTrade !== true) {
      return shouldDisableTrade;
    }
    if (!validationResponse?.isValid) {
      return validationResponse?.reason;
    }
    if (!campainStatusIsValidForAccess) {
      if (campaignMetadata?.status === CAMPAIGN_STATUS.PAUSED) {
        return UneligibleTradeReason.CAMPAIGN_PAUSED;
      }
      if (campaignMetadata?.status === CAMPAIGN_STATUS.PAST) {
        return UneligibleTradeReason.CAMPAIGN_PAST;
      }
      if (campaignMetadata?.status === CAMPAIGN_STATUS.FUTURE) {
        return UneligibleTradeReason.CAMPAIGN_NOT_STARTED;
      }
    }

    return undefined;
  };

  const reason = getReason();

  const nextAccessDate = useMemo(() => {
    if (!reason || !campaignMetadata) return undefined;

    if (reason === UneligibleTradeReason.COOLDOWN_VOLUME_REACHED) {
      return nextWindowDate;
    }

    if (
      reason === UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED ||
      reason === UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED ||
      reason === UneligibleTradeReason.LIMIT_REACHED
    ) {
      const nextEpoch = getNextEpoch(campaignMetadata);
      return nextEpoch?.start;
    }

    return undefined;
  }, [reason, currentEpoch, nextWindowDate]);

  // For onchain campaigns, wallet must meet age requirement
  const walletAgeValid = isOnchain ? isWalletAgeEligible !== false : true;

  return {
    access:
      validationResponse?.isValid &&
      !shouldDisableTrade &&
      campainStatusIsValidForAccess &&
      !hubUser?.isBanned &&
      walletAgeValid,
    reason,
    isLoading:
      isCampaignLoading ||
      isLeaderboardLoading ||
      isValidationLoading ||
      !validationResponse ||
      isUserVolumeInWindowLoading ||
      (isOnchain && isWalletAgeLoading),
    // Keep this stable for blocked/non-live campaigns to avoid rebuilding widget config
    // from a fresh `[]` on every render.
    defiAddresses: validationResponse?.defiAddresses ?? EMPTY_DEFI_ADDRESSES,
    nextAccessDate,
    walletAgeReason: walletAgeData?.reason ?? null,
  };
};
